// Footer.jsx - Shared footer component for all pages
import React from 'react';
import { Link } from 'react-router-dom';
import { Eye } from 'lucide-react';

export default function Footer() {
  return (
    <footer className="relative z-10 bg-slate-900/80 backdrop-blur-xl border-t border-slate-700/50 py-12 px-4 mt-auto">
      <div className="max-w-7xl mx-auto">
        {/* 5 columns since the Colectiv family column was added. Palette here is
            deliberately untouched — the indigo/violet is swept in Phase 1's
            component pass, and changing it now would alter the ungated look. */}
        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-5 gap-8">
          <div>
            <div className="flex items-center space-x-2 mb-4">
              <div className="w-8 h-8 bg-gradient-to-r from-blue-500 to-purple-600 rounded-lg flex items-center justify-center">
                <Eye className="w-5 h-5 text-white" />
              </div>
              <span className="text-xl font-bold text-white">CodeRated</span>
            </div>
            <p className="text-slate-400 text-sm leading-relaxed">
              The future of website intelligence. Powered by multi-agent AI to deliver 
              human-quality insights at unprecedented scale.
            </p>
          </div>
          
          <div>
            <h3 className="text-white font-semibold mb-4">Product</h3>
            <ul className="space-y-2 text-slate-400 text-sm">
              <li><Link to="/#features" className="hover:text-white transition-colors">Features</Link></li>
              <li><Link to="/#pricing" className="hover:text-white transition-colors">Pricing</Link></li>
              <li><Link to="/feed" className="hover:text-white transition-colors">Browse Analyses</Link></li>
              <li><Link to="/search" className="hover:text-white transition-colors">Try Demo</Link></li>
            </ul>
          </div>
          
          <div>
            <h3 className="text-white font-semibold mb-4">Company</h3>
            <ul className="space-y-2 text-slate-400 text-sm">
              <li><Link to="/about" className="hover:text-white transition-colors">About</Link></li>
              <li><Link to="/contact" className="hover:text-white transition-colors">Contact</Link></li>
            </ul>
          </div>
          
          <div>
            <h3 className="text-white font-semibold mb-4">Support</h3>
            <ul className="space-y-2 text-slate-400 text-sm">
              <li><Link to="/help" className="hover:text-white transition-colors">Help Center</Link></li>
              <li><Link to="/api-docs" className="hover:text-white transition-colors">API Reference</Link></li>
              <li><Link to="/status" className="hover:text-white transition-colors">Status</Link></li>
            </ul>
          </div>

          {/* Colectiv family — so the relationship is legible from any page, not
              just /about. External links (different products, different domains),
              hence <a> rather than <Link>. */}
          <div>
            <h3 className="text-white font-semibold mb-4">Colectiv</h3>
            <ul className="space-y-2 text-slate-400 text-sm">
              {[
                { label: 'Colectiv', href: 'https://colectiv.ai' },
                { label: 'Cwave', href: 'https://colectiv.music' },
                { label: 'Aperture', href: 'https://colectiv.camera' },
                { label: 'ArtGuard', href: 'https://colectiv.ai/verify' },
              ].map((p) => (
                <li key={p.label}>
                  <a
                    href={p.href}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="hover:text-white transition-colors"
                  >
                    {p.label}
                  </a>
                </li>
              ))}
            </ul>
          </div>
        </div>

        <div className="border-t border-slate-700/50 mt-12 pt-8 flex flex-col sm:flex-row justify-between items-center">
          {/* Year is derived, not typed — this read "© 2025" for most of 2026. */}
          <p className="text-slate-400 text-sm">
            © {new Date().getFullYear()} CodeRated. All rights reserved.
          </p>
          <div className="flex space-x-6 text-slate-400 text-sm mt-4 sm:mt-0">
            <Link to="/privacy" className="hover:text-white transition-colors">Privacy Policy</Link>
            <Link to="/terms" className="hover:text-white transition-colors">Terms of Service</Link>
            <Link to="/contact" className="hover:text-white transition-colors">Security</Link>
          </div>
        </div>
      </div>
    </footer>
  );
}